import React, { useState, useRef, useEffect } from "react";
import { X, Upload, Trash2, Edit2, Check, Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";
import { useDispatch, useSelector } from "react-redux";
import { bulkImportContacts } from "slices/thunk";
import { createSelector } from "@reduxjs/toolkit";
import { getAllList } from "services/contact.service";
import Loading from "Common/Components/Loading";

interface ImportedContact {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  description: string;
  result: 'Pass' | 'Fail';
  reason: string;
}

const ImportContacts: React.FC = () => {
  document.title = `Import Contacts | EngageHub IQ`;

  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [contacts, setContacts] = useState<ImportedContact[]>([]);
  const [contactList, setContactList] = useState<[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<ImportedContact | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loadingContactList, setLoadingContactList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch<any>();




  useEffect(() => {
    fetchAllContacts();
  }, []);

  const fetchAllContacts = async () => {
    try {
      setLoadingContactList(true);
      const response = await getAllList();
      if (response) {
        setContactList(response);
      }
    } catch (error) {
      toast.error('Failed to fetch contacts');
      setLoadingContactList(false);
    } finally {
      setLoadingContactList(false);
    }
  };

  const validateContacts = (contacts: ImportedContact[]): ImportedContact[] => {
    return contacts.map(contact => {
      const reasons: string[] = [];

      // Check for Display Name - it's required
      if (!contact.displayName || contact.displayName.trim() === '') {
        reasons.push('Missing Display Name');
      }

      // Check for missing required fields (at least one of email or phone)
      const hasContactInfo = contact.email || contact.phone;
      if (!hasContactInfo) {
        reasons.push('Missing required fields: Phone Number or Email');
      }

      // Check format of email and phone
      if (contact.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contact.email)) {
          reasons.push('Invalid email format');
        }
      }

      if (contact.phone) {
        const phoneRegex = /^\+1\d{10}$/; // US country code +1
        if (!phoneRegex.test(contact.phone)) {
          reasons.push('Phone number must start with +1 (USA code) followed by 10 digits');
        }
      }

      // Check for duplicates in database
      if (contactList && contactList.length > 0) {
        const isDuplicateDisplayName = contact.displayName && contactList.some((existingContact: any) =>
          existingContact.name?.toLowerCase() === contact.displayName.toLowerCase()
        );

        const isDuplicateEmail = contact.email && contactList.some((existingContact: any) =>
          existingContact.email?.toLowerCase() === contact.email.toLowerCase()
        );

        const isDuplicatePhone = contact.phone && contactList.some((existingContact: any) =>
          existingContact.phoneNumber === contact.phone
        );

        if (isDuplicateDisplayName) {
          reasons.push('Display name already exists');
        }
        if (isDuplicateEmail) {
          reasons.push('Email already exists');
        }
        if (isDuplicatePhone) {
          reasons.push('Phone number already exists');
        }
      }

      const result: 'Pass' | 'Fail' = reasons.length > 0 ? 'Fail' : 'Pass';

      return {
        ...contact,
        result,
        reason: reasons.join(', ')
      };
    });
  };

  const handleFileSelect = (file: File) => {
    if (file && file.type === 'text/csv') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        parseCSV(text);
      };
      reader.readAsText(file);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const parseCSV = (text: string) => {
    setUploading(true);
    setTimeout(() => {
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length === 0) {
        setUploading(false);
        return;
      }

      // Parse header
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

      // Map headers to expected fields
      const fieldMap: { [key: string]: string } = {
        'display name': 'displayName',
        'displayname': 'displayName',
        'name': 'displayName',
        'first name': 'firstName',
        'firstname': 'firstName',
        'last name': 'lastName',
        'lastname': 'lastName',
        'email': 'email',
        'phone': 'phone',
        'phone number': 'phone',
        'phonenumber': 'phone',
        'company': 'company',
        'address': 'address',
        'description': 'description',
        'notes': 'description',
      };

      const parsedContacts: ImportedContact[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const contact: any = {
          id: `imported-${Date.now()}-${i}`,
          displayName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          address: '',
          description: '',
        };

        headers.forEach((header, index) => {
          const field = fieldMap[header];
          if (field && index < values.length) {
            // Always assign the value, even if empty, to maintain correct column mapping
            contact[field] = values[index] || '';
          }
        });

        // Add all contacts for validation, including those with missing fields
        parsedContacts.push(contact);
      }

      // Validate contacts against database
      const validatedContacts = validateContacts(parsedContacts);
      setContacts(validatedContacts);
      setStep('preview');
      setUploading(false);
    }, 2000);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleRemoveContact = (id: string) => {
    setContacts(contacts.filter(c => c.id !== id));
  };

  const handleEditStart = (contact: ImportedContact) => {
    setEditingId(contact.id);
    setEditValues({ ...contact });
  };

  const handleEditSave = () => {
    if (editValues && editingId) {
      const updatedContacts = contacts.map(c => c.id === editingId ? editValues : c);
      // Revalidate all contacts after edit
      const validatedContacts = validateContacts(updatedContacts);
      setContacts(validatedContacts);
      setEditingId(null);
      setEditValues(null);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditValues(null);
  };

  const handleEditChange = (field: keyof ImportedContact, value: string) => {
    if (editValues) {
      setEditValues({ ...editValues, [field]: value });
    }
  };

  const handleImport = async () => {
    // Only import contacts that pass validation
    const validContacts = contacts.filter(c => c.result === 'Pass');

    if (validContacts.length === 0) {
      toast.error('No valid contacts to import');
      return;
    }

    setIsImporting(true);
    try {
      // Add 2000ms delay before proceeding
      await new Promise(resolve => setTimeout(resolve, 2000));

      const payload = validContacts.map(c => ({
        name: c.displayName,
        firstName: c.firstName,
        lastName: c.lastName,
        email: c.email,
        PhoneNumber: c.phone,
        company: c.company,
        address: c.address,
        description: c.description,
      }));

      const response = await dispatch(bulkImportContacts(payload));
      if (response) {
        const failedCount = contacts.filter(c => c.result === 'Fail').length;
        if (failedCount > 0) {
          toast.success(`Successfully imported ${validContacts.length} contact(s). ${failedCount} record(s) were skipped due to validation errors.`);
        } else {
          toast.success(`Successfully imported ${validContacts.length} contact(s)`);
        }
        navigate('/contact');
      }

    } catch (error: any) {
      toast.error(error?.message || "Failed to import contacts");
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancel = () => {
    navigate('/contact');
  };

  const downloadTemplate = () => {
    const template = 'Display Name,First Name,Last Name,Email,Phone,Company,Address,Description\n' +
      'John Doe,John,Doe,john@example.com,+12025551234,Acme Inc,123 Main St,Sample contact';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contact_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loadingContactList) {
    return <>
      <Loading size="sm" color="blue" isVisible={true} /></>
  }

  return (
    <div className="w-full h-[calc(100vh-70px)] bg-white overflow-hidden">
      <div className="p-4 sm:p-6 h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleCancel}
            className="p-2 ps-0 rounded-md hover:bg-slate-100 dark:hover:bg-zink-600 transition-colors"
            aria-label="Back to contacts"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-zink-200" />
          </button>
          <div>
            <h5 className="text-15 font-semibold">Import Contacts</h5>
            <p className="text-sm text-slate-500 dark:text-zink-300">Upload and import contacts from CSV file</p>
          </div>
        </div>

        {/* Body */}
        <div className="w-full mx-auto">
          {uploading ? (
            <>
              <Loading size="sm" color="blue" isVisible={true} /></>
          ) : (
            <>
              {step === 'upload' && (
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <p className="text-slate-500 text-sm  dark:text-zink-200 mb-2">
                        Upload a CSV file with your contacts. Make sure your file includes headers.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadTemplate}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-blue-500 border border-blue-500 rounded-md hover:bg-blue-50 dark:hover:bg-zink-700 whitespace-nowrap"
                    >
                      <Download className="size-4" />
                      Download Template
                    </button>
                  </div>

                  {/* Drop Zone */}
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-16 text-center transition-colors ${dragActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-zink-700'
                      : 'border-slate-300 dark:border-zink-500 hover:border-slate-400'
                      }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileInput}
                      className="hidden"
                    />

                    <Upload className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                    <p className="mb-2 text-base text-slate-600 dark:text-zink-200">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-sm text-slate-500 dark:text-zink-300 mb-4">CSV file only</p>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-3 text-white bg-blue-500 rounded-md hover:bg-blue-600"
                    >
                      Select File
                    </button>
                  </div>

                  <div className="p-6 rounded-lg bg-slate-50 dark:bg-zink-700">
                    <h6 className="mb-3 text-base font-semibold">CSV Format Requirements:</h6>
                    <ul className="text-sm list-disc list-inside text-slate-600 dark:text-zink-200 space-y-2">
                      <li>First row must contain column headers</li>
                      <li>Supported headers: Display Name, First Name, Last Name, Email, Phone, Company, Address, Description</li>
                      <li>At least Display Name or Email is required for each contact</li>
                      <li>Phone numbers should include country code (e.g., +12025551234)</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h6 className="text-15 font-semibold">
                  Preview Contacts ({contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'})
                </h6>
                <button
                  type="button"
                  onClick={() => setStep('upload')}
                  className="text-sm text-blue-500 hover:text-blue-600 font-medium"
                >
                  Upload Different File
                </button>
              </div>

              {contacts.length === 0 ? (
                <div className="py-16 text-center text-slate-500 border border-slate-200 dark:border-zink-500 rounded-lg">
                  No valid contacts found in the file. Please check your CSV format and try again.
                </div>
              ) : (
                <div className="border border-slate-200 dark:border-zink-500 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto h-[calc(100vh-370px)] min-h-[calc(200px)]">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0 dark:bg-zink-700">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Display Name</th>
                          <th className="px-4 py-3 text-left font-semibold">First Name</th>
                          <th className="px-4 py-3 text-left font-semibold">Last Name</th>
                          <th className="px-4 py-3 text-left font-semibold">Email</th>
                          <th className="px-4 py-3 text-left font-semibold">Phone</th>
                          <th className="px-4 py-3 text-left font-semibold">Address</th>
                          <th className="px-4 py-3 text-left font-semibold">Company</th>
                          <th className="px-4 py-3 text-left font-semibold">Description</th>
                          <th className="px-4 py-3 text-left font-semibold">Result</th>
                          <th className="px-4 py-3 text-left font-semibold">Reason</th>
                          {/* <th className="px-4 py-3 text-center font-semibold w-28">Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((contact) => (
                          <tr
                            key={contact.id}
                            className={`border-b border-slate-200 dark:border-zink-500 ${contact.result === 'Fail'
                              ? 'bg-red-50 dark:bg-red-900/20'
                              : 'hover:bg-slate-50 dark:hover:bg-zink-600'
                              }`}
                          >
                            {editingId === contact.id ? (
                              <>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.displayName || ''}
                                    onChange={(e) => handleEditChange('displayName', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.firstName || ''}
                                    onChange={(e) => handleEditChange('firstName', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.lastName || ''}
                                    onChange={(e) => handleEditChange('lastName', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="email"
                                    value={editValues?.email || ''}
                                    onChange={(e) => handleEditChange('email', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.phone || ''}
                                    onChange={(e) => handleEditChange('phone', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.address || ''}
                                    onChange={(e) => handleEditChange('address', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.company || ''}
                                    onChange={(e) => handleEditChange('company', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={editValues?.description || ''}
                                    onChange={(e) => handleEditChange('description', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded dark:bg-zink-700 dark:border-zink-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 text-xs font-semibold rounded ${editValues?.result === 'Pass'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                    {editValues?.result}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-red-600 dark:text-red-400">
                                  {editValues?.reason}
                                </td>
                                {/* <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={handleEditSave}
                                      className="p-1.5 text-green-500 hover:text-green-600 hover:bg-green-50 rounded"
                                      title="Save"
                                    >
                                      <Check className="size-4" />
                                    </button>
                                    <button
                                      onClick={handleEditCancel}
                                      className="p-1.5 text-slate-500 hover:text-slate-600 hover:bg-slate-100 rounded"
                                      title="Cancel"
                                    >
                                      <X className="size-4" />
                                    </button>
                                  </div>
                                </td> */}
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3">{contact.displayName}</td>
                                <td className="px-4 py-3">{contact.firstName}</td>
                                <td className="px-4 py-3">{contact.lastName}</td>
                                <td className="px-4 py-3">{contact.email}</td>
                                <td className="px-4 py-3">{contact.phone}</td>
                                <td className="px-4 py-3">{contact.address}</td>
                                <td className="px-4 py-3">{contact.company}</td>
                                <td className="px-4 py-3">{contact.description}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 text-xs font-semibold rounded ${contact.result === 'Pass'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                    {contact.result}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-red-600 dark:text-red-400">
                                  {contact.reason}
                                </td>
                                {/* <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-2">
                                    {contact.result === 'Pass' ? (
                                      <>
                                        <button
                                          onClick={() => handleEditStart(contact)}
                                          className="p-1.5 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                          title="Edit"
                                        >
                                          <Edit2 className="size-4" />
                                        </button>
                                        <button
                                          onClick={() => handleRemoveContact(contact.id)}
                                          className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded"
                                          title="Remove"
                                        >
                                          <Trash2 className="size-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <span className="text-xs text-slate-400 dark:text-zink-400">No actions</span>
                                    )}
                                  </div>
                                </td> */}
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {contacts.length > 0 && (
                <div className="space-y-3">
                  {/* Validation Summary */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-zink-700">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 text-sm font-semibold rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {contacts.filter(c => c.result === 'Pass').length} Pass
                        </span>
                        <span className="px-3 py-1 text-sm font-semibold rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          {contacts.filter(c => c.result === 'Fail').length} Fail
                        </span>
                      </div>
                      {(contacts.length === 0 || contacts.some(c => c.result === 'Fail') || contacts.some(c => c.result === 'Pass')) && (
                        <span className="text-sm text-amber-600 dark:text-amber-400">
                          Failed records will be skipped during import
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setStep('upload')}
                      className="px-6 py-2.5 text-slate-500 border border-slate-300 rounded-md hover:bg-slate-50 dark:border-zink-500 dark:hover:bg-zink-700"
                      disabled={isImporting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleImport}
                      disabled={isImporting || contacts.filter(c => c.result === 'Pass').length === 0}
                      className="px-6 py-2.5 text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting ? 'Importing...' : `Proceed`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default ImportContacts;

